import {
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { RootState } from '../store'
import { useDispatch, useSelector } from 'react-redux'
import { User } from '../models/Organization'
import React, { ChangeEvent, useEffect, useState } from 'react'
import CustomDropdown from './CustomDropdown'
import { Can } from '../utils/AbilityUtil'
import {
  architectureRoles,
  AttributeActionNames,
  AttributeResourceNames,
  engineeringRoles,
  UserRole,
  UserRoleNames,
} from '../models/AccessControl'
import { updateUserAsync } from '../services/usersService'
import { createSelector } from '@reduxjs/toolkit'
import { showErrorMessage } from '../actions/organization'
import { resetPassword } from 'aws-amplify/auth'

const userRolesSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.userRoles,
)
const userColorsSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.userColors,
)
const organizationDomainSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.selectedOrganization?.id,
)

interface UsersInfoProps {
  user: User
}

const UserInfoComponent: React.FC<UsersInfoProps> = ({ user }) => {
  const dispatch = useDispatch<any>()
  const userRoles = useSelector(userRolesSelector) ?? []
  const usersColors = useSelector(userColorsSelector) ?? []
  const organizationDomain = useSelector(organizationDomainSelector) ?? ''
  const filteredUserRoles = !!user
    ? architectureRoles.includes(user.role.name)
      ? userRoles.filter(
          (userRole) =>
            architectureRoles.includes(userRole.name) ||
            userRole.name === UserRoleNames.Viewer,
        )
      : engineeringRoles.includes(user.role.name)
        ? userRoles.filter(
            (userRole) =>
              engineeringRoles.includes(userRole.name) ||
              userRole.name === UserRoleNames.Viewer,
          )
        : userRoles
    : userRoles

  const createdDate = user.createdAt ? new Date(user.createdAt) : new Date()
  const month = createdDate.toLocaleString('default', { month: 'long' })
  const year = createdDate.getUTCFullYear()

  const [userName, setUserName] = useState<string>(user.user_name)
  const [userEmail, setUserEmail] = useState<string>(user.email)
  const [userSurname, setUserSurname] = useState<string>('Surname')
  const [userRole, setUserRole] = useState<UserRole>(user.role)

  const isUserEmailValid = (user: User) => {
    return user.email.includes(organizationDomain)
  }

  const handleUserNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setUserName(event?.target.value)
  }

  const handleUserEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setUserEmail(event?.target.value)
  }

  const handleUserSurnameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setUserSurname(event?.target.value)
  }

  const handleUserRoleClick = (userRole: UserRole) => {
    setUserRole(userRole)
  }

  const handleSave = () => {
    if (!isUserEmailValid(user)) {
      dispatch(showErrorMessage('email must be from same company'))
      return
    }

    dispatch(
      updateUserAsync({
        userId: user.id,
        info: { role_id: userRole.id, name: userName, email: userEmail },
      }),
    )
  }

  const handleResetPassword = async () => {
    await resetPassword({ username: user.user_name })
  }

  useEffect(() => {
    setUserName(user.user_name)
    setUserEmail(user.email)
    setUserSurname('Surname')
  }, [user])

  return (
    <Can I={AttributeActionNames.Update} a={AttributeResourceNames.Users}>
      {(allowed) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            gap: '0.75rem',
            pt: '3.75rem',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.94rem',
              fontWeight: 500,
              lineHeight: 'normal',
              color: '#06004A',
            }}
          >
            Member info
          </Typography>
          <Box
            sx={{
              background: '#F5F5F5',
              borderRadius: '0.4rem',
              boxShadow: '0px 3px 5px 0px #2F2F2F1A;',
              height: '23rem',
              width: `calc(100% - 2.5rem)`,
              display: 'flex',
              flexDirection: 'column',
              p: '0.75rem 1.25rem 1.25rem 1.25rem',
            }}
          >
            <Box
              sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}
            >
              <Stack gap='1.5rem'>
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.64rem',
                        fontWeight: 500,
                        lineHeight: 'normal',
                        color: '#BDBDBD',
                      }}
                    >
                      Added {month}, {year}
                    </Typography>
                  </Box>
                  <Box
                    gap={'1rem'}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='2.5em'
                      height='2.5em'
                      viewBox='0 0 32 32'
                      fill='none'
                    >
                      <circle
                        cx='16'
                        cy='16'
                        r='16'
                        fill={
                          usersColors?.find((usr) => usr.id === user.id)
                            ?.color ?? '#04002E'
                        }
                      />
                      <text
                        x='50%'
                        y='50%'
                        textAnchor='middle'
                        dy='.4em'
                        fontSize='1em'
                        fill='#fff'
                        fontWeight='bold'
                      >
                        {user.user_name
                          .split(' ')
                          .map((item) => item.charAt(0))
                          .join('')
                          .toUpperCase()}
                      </text>
                    </svg>
                    <Typography
                      sx={{
                        fontSize: '0.88rem',
                        fontWeight: 500,
                        lineHeight: 'normal',
                        color: '#2F2F2F',
                      }}
                    >
                      {user.user_name}
                    </Typography>
                  </Box>
                </Box>
                <Stack gap='1rem'>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <TextField
                      sx={{
                        width: '47%',
                        '& .MuiInputBase-input': {
                          color: '#4F4F4F',
                          fontSize: '0.88rem',
                          fontWeight: 500,
                          pb: '0.5rem',
                        },
                        '& .MuiInput-underline:before': {
                          borderBottomColor: '#9D9D9D',
                        },
                        '& .MuiInput-underline:after': {
                          borderBottomColor: '#1705E5',
                        },
                      }}
                      size='small'
                      required
                      margin='dense'
                      variant='standard'
                      id='userName'
                      name='userName'
                      value={userName}
                      onChange={handleUserNameChange}
                    />
                    <TextField
                      sx={{
                        width: '47%',
                        '& .MuiInputBase-input': {
                          color: '#4F4F4F',
                          fontSize: '0.88rem',
                          fontWeight: 500,
                          pb: '0.5rem',
                        },
                        '& .MuiInput-underline:before': {
                          borderBottomColor: '#9D9D9D',
                        },
                        '& .MuiInput-underline:after': {
                          borderBottomColor: '#1705E5',
                        },
                      }}
                      size='small'
                      required
                      margin='dense'
                      variant='standard'
                      id='userName'
                      name='userName'
                      value={userSurname}
                      onChange={handleUserSurnameChange}
                    />
                  </Box>
                  <TextField
                    sx={{
                      '& .MuiInputBase-input': {
                        color: '#4F4F4F',
                        fontSize: '0.88rem',
                        fontWeight: 500,
                        pb: '0.5rem',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#9D9D9D',
                      },
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#1705E5',
                      },
                    }}
                    size='small'
                    required
                    margin='dense'
                    variant='standard'
                    id='userEmail'
                    name='userEmail'
                    value={userEmail}
                    onChange={handleUserEmailChange}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      borderBottom: '1px solid #9D9D9D',
                      py: '0.5rem',
                      '&:hover': {
                        borderBottom: '2px solid #000',
                      },
                    }}
                  >
                    <CustomDropdown
                      disabled={!allowed}
                      title={userRole.name ?? 'Select role'}
                      color='#4F4F4F'
                      buttonStyleProps={{
                        justifyContent: 'space-between',
                        padding: 0,
                        background: 'transparent',
                        textTransform: 'capitalize',
                        width: '98%',
                      }}
                      titleStyleProps={{
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'capitalize',
                      }}
                    >
                      {filteredUserRoles.map((item, index) => (
                        <MenuItem
                          key={index}
                          onClick={() => handleUserRoleClick(item)}
                          sx={{
                            color: '#04002E',
                            '&:hover': {
                              backgroundColor: '#1705E5',
                              color: '#fff',
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '12px',
                              textTransform: 'capitalize',
                            }}
                          >
                            {item.name.toLowerCase()}
                          </Typography>
                        </MenuItem>
                      ))}
                    </CustomDropdown>
                  </Box>
                </Stack>
              </Stack>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                justifyContent: 'flex-end',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: '0.75rem' }}>
                  <Button
                    disableRipple
                    variant='outlined'
                    size='large'
                    sx={{
                      background: '#fff',
                      borderColor: '#D9D9D9',
                      color: '#FF3B47',
                      textTransform: 'none',
                      fontSize: '0.64rem',
                      lineHeight: 'normal',
                      p: '0.5rem 0.75rem',
                    }}
                  >
                    Delete Member
                  </Button>
                  <Button
                    disableRipple
                    size='large'
                    onClick={handleResetPassword}
                    sx={{
                      background: '#fff',
                      color: '#000',
                      textTransform: 'none',
                      fontSize: '0.64rem',
                      lineHeight: 'normal',
                      p: '0.5rem 0.75rem',
                    }}
                  >
                    Reset Password
                  </Button>
                </Box>
                <Button
                  disableRipple
                  size='large'
                  onClick={handleSave}
                  sx={{
                    background: '#56003D',
                    color: '#fff',
                    textTransform: 'none',
                    fontSize: '0.64rem',
                    lineHeight: 'normal',
                    p: '0.5rem 0.75rem',
                  }}
                >
                  Save Changes
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Can>
  )
}

export default UserInfoComponent
